/**
 * 24h × 7d heatmap of historical reply rates, sourced from the send_timing
 * recommendation grid (k≥100 suppressed cells). Used in Engagement Insights.
 *
 * Reuses the same hook in OptimalSendTimeBadge (campaign builder anchor B)
 * so React Query dedupes the request.
 */

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useSendTimingRecs } from '@/hooks/ml';
import type { SendTimingMetric } from '@/hooks/ml';
import { ModelMetadataBanner, CellSuppressionBanner } from '@/components/ml';

const METRIC_LABEL: Record<SendTimingMetric, string> = {
  engagement_rate: 'Engagement (reply or click)',
  reply_rate: 'Reply rate',
  click_rate: 'Click rate',
};

const HOURS = Array.from({ length: 24 }, (_, h) => h);
const DOWS = [
  { idx: 1, label: 'Mon' },
  { idx: 2, label: 'Tue' },
  { idx: 3, label: 'Wed' },
  { idx: 4, label: 'Thu' },
  { idx: 5, label: 'Fri' },
  { idx: 6, label: 'Sat' },
  { idx: 0, label: 'Sun' },
];

function colorFor(rate: number, max: number): string {
  if (max <= 0) return 'bg-gray-100';
  const normalized = rate / max;
  if (normalized >= 0.85) return 'bg-emerald-600 text-white';
  if (normalized >= 0.6) return 'bg-emerald-400 text-white';
  if (normalized >= 0.4) return 'bg-amber-300';
  if (normalized >= 0.2) return 'bg-amber-100';
  if (normalized > 0) return 'bg-gray-100';
  return 'bg-gray-50 text-gray-400';
}

export const OptimalSendTimeHeatmapCard: React.FC = () => {
  const [metric, setMetric] = useState<SendTimingMetric>('engagement_rate');
  // Top 500 cells gives full grid coverage.
  const { data, isLoading, isError, error } = useSendTimingRecs({ topN: 500, metric });
  const notTrained =
    isError && error instanceof Error && error.message === 'model_not_trained';

  // Build (dow, hour) -> { rate, sends } using the active metric.
  const byCell = new Map<string, { rate: number; sends: number }>();
  let maxRate = 0;
  for (const c of data?.cells ?? []) {
    const key = `${c.send_dow}|${c.send_hour}`;
    const existing = byCell.get(key);
    const cellRate = c[metric];
    if (!existing || cellRate > existing.rate) {
      byCell.set(key, { rate: cellRate, sends: c.sends });
      if (cellRate > maxRate) maxRate = cellRate;
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">
          Best send time — {METRIC_LABEL[metric].toLowerCase()}
        </CardTitle>
        <select
          value={metric}
          onChange={(e) => setMetric(e.target.value as SendTimingMetric)}
          className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs"
        >
          <option value="engagement_rate">Engagement (reply or click)</option>
          <option value="reply_rate">Reply only</option>
          <option value="click_rate">Click only</option>
        </select>
      </CardHeader>
      <CardContent className="space-y-3">
        <ModelMetadataBanner modelName="send_timing" />
        <CellSuppressionBanner />
        {isLoading ? (
          <div className="text-sm text-gray-500">Loading…</div>
        ) : notTrained ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            The send-timing model artifact is not yet deployed to production.
            Once the ML training cron + S3 bucket are configured (see
            <code className="mx-1 rounded bg-white px-1 py-0.5 text-xs">docs/legacy_trust/phase6b_metabase_setup.md</code>),
            this heatmap will populate with the best send-windows per language.
          </div>
        ) : !data?.cells?.length ? (
          <div className="text-sm text-gray-500">
            No recommendation data available.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="text-xs">
              <thead>
                <tr>
                  <th className="px-1 py-0.5 text-left text-gray-500 font-normal">&nbsp;</th>
                  {HOURS.map((h) => (
                    <th key={h} className="px-0.5 py-0.5 text-center text-gray-500 font-normal" style={{ minWidth: 22 }}>
                      {h % 3 === 0 ? h : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {DOWS.map(({ idx, label }) => (
                  <tr key={idx}>
                    <td className="pr-2 py-0.5 text-right text-gray-600">{label}</td>
                    {HOURS.map((h) => {
                      const cell = byCell.get(`${idx}|${h}`);
                      const cls = colorFor(cell?.rate ?? 0, maxRate);
                      const title = cell
                        ? `${label} ${h}:00 — ${(cell.rate * 100).toFixed(1)}% ${METRIC_LABEL[metric].toLowerCase()} · ${cell.sends.toLocaleString()} sends`
                        : `${label} ${h}:00 — suppressed (<100 sends)`;
                      return (
                        <td
                          key={h}
                          title={title}
                          className={`text-center px-0.5 py-0.5 rounded-sm border border-white ${cls}`}
                          style={{ minWidth: 22 }}
                        >
                          {cell ? `${Math.round(cell.rate * 100)}` : ''}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-2 text-xs text-gray-500">
              Cells show {METRIC_LABEL[metric].toLowerCase()} % over the last 12 months. Hover any cell for sample size. Languages used:{' '}
              <span className="font-medium">{(data.languages_used || []).join(', ') || '—'}</span>.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
